import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';

import { Grid, Typography } from '@mui/material';

import { TranslationKeys } from 'app/translations';

import format from 'app/utils/formatter';

import { useEmploymentContractTypeSelectItems } from 'app/hooks';
import { InputFieldFactory } from 'app/components/FormikField';
import { FormikModal } from 'app/components';

import type { EmployeeEmploymentContractFormValues } from '../../EmployeeEmploymentContractFormDialog';
import { EmployeeEmploymentContractFields } from '../../../../EmployeeEmploymentContracts.types';

type EmployeeEmploymentContractFormDialogBodyProps = {
  open: boolean;
  isEdit: boolean;
  closeDialog: () => void;
};

export const EmployeeEmploymentContractFormDialogBody = ({
  open,
  isEdit,
  closeDialog,
}: EmployeeEmploymentContractFormDialogBodyProps) => {
  const { initialValues, submitForm, resetForm } = useFormikContext<EmployeeEmploymentContractFormValues>();
  const { employmentContractTypeSelectItems } = useEmploymentContractTypeSelectItems();

  const onClickSubmit = React.useCallback(() => {
    submitForm();
  }, [submitForm]);

  return (
    <FormikModal
      title={
        <FormattedMessage
          id={isEdit ? TranslationKeys.employees_actions_editContract : TranslationKeys.employees_actions_addContract}
        />
      }
      show={open}
      onSubmit={onClickSubmit}
      onCancel={isEdit ? closeDialog : undefined}
      onHide={() => {
        closeDialog();
        resetForm();
      }}
      disableEnforceFocus
    >
      {isEdit && (
        <Typography variant="h6" paragraph>
          {format.date(initialValues.startDate)}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputFieldFactory
            field={{
              type: 'date',
              header: EmployeeEmploymentContractFields.StartDate.Label,
              name: EmployeeEmploymentContractFields.StartDate.InputName,
              options: EmployeeEmploymentContractFields.StartDate.Options,
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <InputFieldFactory
            field={{
              type: 'date',
              header: EmployeeEmploymentContractFields.EndDate.Label,
              name: EmployeeEmploymentContractFields.EndDate.InputName,
              options: EmployeeEmploymentContractFields.EndDate.Options,
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InputFieldFactory
            field={{
              type: 'select',
              header: EmployeeEmploymentContractFields.ContractType.Label,
              name: EmployeeEmploymentContractFields.ContractType.InputName,
              required: true,
              items: employmentContractTypeSelectItems,
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InputFieldFactory
            field={{
              type: 'text',
              header: EmployeeEmploymentContractFields.ContractId.Label,
              name: EmployeeEmploymentContractFields.ContractId.InputName,
              required: true,
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputFieldFactory
            field={{
              type: 'date',
              header: EmployeeEmploymentContractFields.CreationDate.Label,
              name: EmployeeEmploymentContractFields.CreationDate.InputName,
              options: EmployeeEmploymentContractFields.CreationDate.Options,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <InputFieldFactory
            field={{
              type: 'text',
              header: EmployeeEmploymentContractFields.EmploymentSequence.Label,
              name: EmployeeEmploymentContractFields.EmploymentSequence.InputName,
            }}
          />
        </Grid>
      </Grid>
    </FormikModal>
  );
};
